import {
  Box,
  Button,
  Chip,
  Grid,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import RepeatIcon from "@mui/icons-material/Repeat";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import CardContent from "@mui/material/CardContent/CardContent";
import * as React from "react";
import {
  List,
  Datagrid,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  Show,
  SimpleShowLayout,
  FunctionField,
  ListProps,
  EditProps,
  CreateProps,
  ShowProps,
  FilterLiveSearch,
  FilterList,
  FilterListItem,
  ReferenceField,
  SavedQueriesList,
  useDataProvider,
  useNotify,
  useShowController,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { Cargo } from "../models/Cargo";
import { CargoCard } from "./components/cargo-card";
import {
  AsideStyledCard,
  CargoNameWithImageField,
  UserAvatarWithName,
  fixedRightAsideSx,
  fixedRightAsideListSx,
} from "./components/common";
import { User } from "../models/common";

const MAP_DRIVER_FETCH_FAILED_MESSAGE = "获取货源详情失败，无法打开地图司机";
const MAP_DRIVER_MISSING_COORDINATES_MESSAGE =
  "当前货源详情缺少始发地坐标，无法打开地图司机";

const CARGO_STATUS_CHOICES = [
  { id: 1, name: "上架中" },
  { id: 2, name: "主动下架" },
  { id: 3, name: "超时下架" },
];

const getMapNavigationPayload = (cargo: Cargo | null | undefined) => {
  const coordinates = cargo?.from?.location?.coordinates;
  const longitude = cargo?.from?.longitude;
  const latitude = cargo?.from?.latitude;

  const lon =
    Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates[0]
      : longitude;
  const lat =
    Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates[1]
      : latitude;

  if (typeof lat !== "number" || typeof lon !== "number") {
    return null;
  }

  const address = [
    cargo?.from?.province,
    cargo?.from?.city,
    cargo?.from?.district,
    cargo?.from?.address,
  ]
    .filter(Boolean)
    .join("");

  return {
    lat,
    lon,
    address,
  };
};

const getCargoStatusName = (status: number | undefined) =>
  CARGO_STATUS_CHOICES.find((choice) => choice.id === status)?.name ?? "-";

const formatCargoRoute = (cargo: Cargo) =>
  `${cargo.from.city}${cargo.from.district ? `·${cargo.from.district}` : ""} → ${cargo.to.city}${cargo.to.district ? `·${cargo.to.district}` : ""}`;

const formatCargoTime = (value: string | Date | null | undefined) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

const formatCargoDepartureTime = (cargo: Cargo) => {
  if (!cargo.expect_departure_at) {
    return "-";
  }

  const date = new Date(cargo.expect_departure_at);
  const pad = (n: number) => String(n).padStart(2, "0");

  if (cargo.continuous) {
    return `每天 ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  }

  return formatCargoTime(cargo.expect_departure_at);
};

const getDepositLabel = (cargo: Cargo) => {
  if (!cargo.deposit) {
    return "无押金";
  }

  return `押金 ${(cargo.deposit / 100).toFixed(0)} 元`;
};

const CargoUserReferenceField = ({
  source,
  label,
}: {
  source: "owner" | "poster";
  label: string;
}) => (
  <FunctionField
    label={label}
    source={source}
    render={(record: Cargo) => {
      const user = record[source];

      if (!user) {
        return "-";
      }

      if (typeof user === "object") {
        return <UserAvatarWithName record={user as User} />;
      }

      return (
        <ReferenceField
          reference="users"
          source={source}
          sortBy="name"
          link={false}
        >
          <FunctionField
            render={(record: User) =>
              record ? <UserAvatarWithName record={record} /> : "-"
            }
          />
        </ReferenceField>
      );
    }}
  />
);

const Aside = ({
  selectedCargo,
  isNavigating,
  onGoToMapDrivers,
}: {
  selectedCargo: Cargo | null;
  isNavigating: boolean;
  onGoToMapDrivers: () => void;
}) => {
  return (
    <AsideStyledCard
      sx={{
        ...fixedRightAsideSx,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <FilterLiveSearch source="name" label="搜索货物名" alwaysOn />
        <FilterLiveSearch source="from.city" label="始发地" />
        <FilterLiveSearch source="to.city" label="目的地" />
        <SavedQueriesList />
        <FilterList label="货源状态" icon={<InventoryIcon fontSize="small" />}>
          <FilterListItem label="全部" value={{ status: undefined }} />
          {CARGO_STATUS_CHOICES.map((choice) => (
            <FilterListItem
              key={choice.id}
              label={choice.name}
              value={{ status: choice.id }}
            />
          ))}
        </FilterList>
        <FilterList label="长期货源" icon={<RepeatIcon fontSize="small" />}>
          <FilterListItem label="全部" value={{ continuous: undefined }} />
          <FilterListItem label="长期货源" value={{ continuous: true }} />
          <FilterListItem label="非长期货源" value={{ continuous: false }} />
        </FilterList>
        <Stack
          spacing={1.5}
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <RoomOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">地图司机</Typography>
          </Stack>
          {selectedCargo ? (
            <Button
              variant="contained"
              fullWidth
              onClick={onGoToMapDrivers}
              disabled={isNavigating}
              sx={{ borderRadius: 2, py: 1 }}
            >
              {isNavigating ? "正在获取坐标..." : "查看地图司机"}
            </Button>
          ) : null}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              当前选择
            </Typography>
            {selectedCargo ? (
              <CargoNameWithImageField cargo={selectedCargo} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                请先选择一条货源
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </AsideStyledCard>
  );
};

export const CargoList: React.FC<ListProps> = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [selectedCargo, setSelectedCargo] = React.useState<Cargo | null>(null);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const selectedCargoId = selectedCargo?.id;

  const handleSelectCargo = React.useCallback((cargo: Cargo) => {
    setSelectedCargo(cargo);
  }, []);

  const handleGoToMapDrivers = React.useCallback(async () => {
    if (!selectedCargo?.id || isNavigating) {
      return;
    }

    setIsNavigating(true);

    try {
      const { data } = await dataProvider.getOne("validCargos", {
        id: selectedCargo.id,
      });
      const detailCargo = data as Cargo;
      const mapNavigationPayload = getMapNavigationPayload(detailCargo);

      if (!mapNavigationPayload) {
        notify(MAP_DRIVER_MISSING_COORDINATES_MESSAGE, { type: "warning" });
        return;
      }

      const searchParams = new URLSearchParams({
        lat: String(mapNavigationPayload.lat),
        lon: String(mapNavigationPayload.lon),
        address: mapNavigationPayload.address,
        distance: "50",
      });

      navigate(`/map/map_drivers?${searchParams.toString()}`);
    } catch (error) {
      notify(MAP_DRIVER_FETCH_FAILED_MESSAGE, { type: "error" });
    } finally {
      setIsNavigating(false);
    }
  }, [dataProvider, isNavigating, navigate, notify, selectedCargo]);

  return (
    <List
      {...props}
      aside={
        <Aside
          selectedCargo={selectedCargo}
          isNavigating={isNavigating}
          onGoToMapDrivers={handleGoToMapDrivers}
        />
      }
      sx={fixedRightAsideListSx}
      filterDefaultValues={{ status: 1 }}
      sort={{ field: "id", order: "DESC" }}
    >
      <Datagrid
        rowClick="show"
        bulkActionButtons={false}
        optimized
        size="small"
        sx={{
          "& .RaDatagrid-headerCell": {
            px: 1.5,
            py: 1,
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
          },
          "& .RaDatagrid-rowCell": {
            px: 1.5,
            py: 1.25,
            verticalAlign: "top",
          },
          "& .column-id": { width: 88 },
          "& .column-select": { width: 68 },
          "& .column-owner": { minWidth: 148 },
        }}
      >
        <FunctionField
          label="选择"
          sortBy={undefined}
          render={(record: Cargo) => (
            <Radio
              size="small"
              checked={record.id === selectedCargoId}
              onClick={(event) => {
                event.stopPropagation();
                handleSelectCargo(record);
              }}
            />
          )}
        />
        <FunctionField
          label="货源信息"
          sortBy="cargo.name"
          render={(record: Cargo) => (
            <Stack spacing={1} sx={{ minWidth: 240, maxWidth: 320 }}>
              <Typography variant="caption" color="text.secondary">
                货号 #{record.id}
              </Typography>
              <CargoNameWithImageField cargo={record} />
            </Stack>
          )}
        />
        <FunctionField
          label="线路 / 时间"
          render={(record: Cargo) => (
            <Stack spacing={0.9} sx={{ minWidth: 200 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {formatCargoRoute(record)}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={getCargoStatusName(record.status)}
                  color={record.status === 1 ? "success" : "default"}
                  variant={record.status === 1 ? "filled" : "outlined"}
                />
                <Chip
                  size="small"
                  label={record.continuous ? "长期货源" : "单次货源"}
                  color={record.continuous ? "primary" : "default"}
                  variant={record.continuous ? "filled" : "outlined"}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                装车 {formatCargoDepartureTime(record)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                发布 {formatCargoTime(record.publish_at)}
              </Typography>
            </Stack>
          )}
        />
        <FunctionField
          label="报价信息"
          render={(record: Cargo) => (
            <Stack spacing={0.5} sx={{ minWidth: 120 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {record.single_price != null
                  ? `${record.single_price} 元/车`
                  : record.unit_price != null
                    ? `${record.unit_price} 元/吨`
                    : "-"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.single_price != null ? "一口价" : record.unit_price != null ? "按吨计价" : "未设置"}
              </Typography>
              <Typography variant="caption" fontWeight={600} color="text.primary">
                
              </Typography>
              <Typography variant="caption" fontWeight={600} color="text.primary">
                押金信息：
              </Typography>
              <Typography variant="caption" color="text.primary">
                {getDepositLabel(record)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.deposit_refund ? "退还押金" : "不退押金"}
              </Typography>
            </Stack>
          )}
        />
        <CargoUserReferenceField source="owner" label="货主" />
        <CargoUserReferenceField source="poster" label="发货人" />
      </Datagrid>
    </List>
  );
};

export const CargoEdit: React.FC<EditProps> = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput source="name" label="货物名" />
      <TextInput source="description" label="货物描述" />
      <TextInput source="unit_price" label="货物单价" />
    </SimpleForm>
  </Edit>
);

export const CargoCreate: React.FC<CreateProps> = (props) => (
  <Create {...props}>
    <SimpleForm>
      <TextInput source="name" label="货物名" />
      <TextInput source="description" label="货物描述" />
      <TextInput source="unit_price" label="货物单价" />
    </SimpleForm>
  </Create>
);

export const CargoShow: React.FC<ShowProps> = (props) => {
  const { record } = useShowController(props);
  return (
    <Show {...props}>
      <SimpleShowLayout>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <CargoCard cargo={record} />
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};
